/**
 * One streamed AI turn over NDJSON: `status` lines while the server works, then one `reply` or
 * `error` line. Shared by the AI player search and the app-wide assistant, so the stream, abort and
 * deadline rules live once. Each caller supplies how to read its reply and how to word its errors.
 */
import { apiRequest } from './api';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value : null);

export interface NdjsonTurnOptions<TReply> {
  endpoint: string;
  body: unknown;
  /** The whole turn, headers and stream: `apiRequest`'s own timeout stops once headers arrive. */
  timeoutMs: number;
  parseReply: (raw: unknown) => TReply;
  /** An `error` line from the server, as the caller's error type. */
  serverError: (code: string | null, message: string | null) => Error;
  /** Anything else that failed (an `ApiError`, a dropped stream), as the caller's error type. */
  failure: (err: unknown) => Error;
  /** The turn ran past `timeoutMs`. */
  timeout: () => Error;
  onStatus: (text: string) => void;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/**
 * Resolves with the reply; rejects with the caller's error, or an `AbortError` when `signal` aborts.
 * A stream that ends without a reply rejects with `failure(null)`.
 */
export const streamNdjsonTurn = async <TReply>(options: NdjsonTurnOptions<TReply>): Promise<TReply> => {
  const { endpoint, body, timeoutMs, parseReply, serverError, failure, timeout, onStatus, signal, headers } = options;
  const turn = new AbortController();
  let timedOut = false;
  const deadline = setTimeout(() => {
    timedOut = true;
    turn.abort();
  }, timeoutMs);
  const follow = () => turn.abort();
  signal?.addEventListener('abort', follow, { once: true });
  if (signal?.aborted) turn.abort();
  const done = () => {
    clearTimeout(deadline);
    signal?.removeEventListener('abort', follow);
  };
  const fail = (err: unknown) => {
    if (timedOut) return timeout();
    if (signal?.aborted) return new DOMException('Aborted', 'AbortError');
    return failure(err);
  };

  let response: Response;
  try {
    response = await apiRequest<Response>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
      responseType: 'response',
      signal: turn.signal,
      headers,
    });
  } catch (err) {
    done();
    throw fail(err);
  }

  if (!response.body) {
    done();
    throw failure(null);
  }
  const reader = response.body.getReader();
  turn.signal.addEventListener('abort', () => void reader.cancel().catch(() => undefined), { once: true });
  const decoder = new TextDecoder();
  let buffer = '';
  const handle = (line: string): { reply: TReply } | null => {
    if (!line.trim()) return null;
    let event: unknown;
    try {
      event = JSON.parse(line);
    } catch {
      return null;
    }
    if (!isRecord(event)) return null;
    if (event.type === 'status') {
      const text = str(event.text);
      if (text) onStatus(text);
      return null;
    }
    if (event.type === 'reply') return { reply: parseReply(event.reply) };
    if (event.type === 'error') throw serverError(str(event.code), str(event.message));
    return null;
  };

  try {
    for (;;) {
      const { done: ended, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !ended });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        const handled = handle(line);
        if (handled) return handled.reply;
      }
      if (ended) {
        const handled = handle(buffer);
        if (handled) return handled.reply;
        throw failure(null);
      }
    }
  } catch (err) {
    throw fail(err);
  } finally {
    done();
    try {
      reader.releaseLock();
    } catch {
      // Already released by a cancel.
    }
  }
};
